import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Drawer, Form, Input, Button, Select, Table, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import API from "../../api"; // Your API instance

const { Option } = Select;

const InventoryForm = ({ open, setOpen, initialValue, isEdit, onSubmit }) => {
  const [form] = Form.useForm();
  const [isLoading, setLoading] = useState(false);
  const [stores, setstores] = useState([]);
  const [products, setProducts] = useState([]);
  const [productsInOrder, setProductsInOrder] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Fetch stores
  const getBranches = useCallback(async () => {
    try {
      const response = await API.get("/stores");
      setstores(response?.data);
    } catch (err) {
      message.error("Failed to fetch stores. Please try again.");
    }
  }, []);

  // Fetch products
  const getProducts = useCallback(async () => {
    try {
      const response = await API.get("/products");
      setProducts(response.data);
    } catch (err) {
      message.error("Failed to fetch products. Please try again.");
    }
  }, []);

  // Populate form with initial values
  useEffect(() => {
    if (open) {
      getBranches();
      getProducts();

      if (isEdit && initialValue) {
        form.setFieldsValue({
          storeId: initialValue.storeId?._id,
        });
        setProductsInOrder(initialValue?.products || []);
      } else {
        form.resetFields();
        setProductsInOrder([]);
      }
    }
  }, [open, isEdit, initialValue, getBranches, getProducts]);

  // Calculate total stock
  const totalStock = useMemo(
    () => productsInOrder.reduce((total, item) => total + item.quantity, 0),
    [productsInOrder]
  );

  useEffect(() => {
    form.setFieldsValue({ totalStock });
  }, [totalStock, form]);

  const availableProducts = useMemo(
    () =>
      products.filter(
        (product) =>
          !productsInOrder.some((item) => item.productId === product?._id)
      ),
    [products, productsInOrder]
  );

  const onFinish = async (values) => {
    setLoading(true);
    const payload = { ...values, products: productsInOrder };

    try {
      await onSubmit(payload);
      setOpen(false);
      form.resetFields();
      setProductsInOrder([]);
    } catch (error) {
      message.error(error.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (productId, quantity) => {
    setProductsInOrder((prev) =>
      prev.map((item) =>
        item.productId === productId
          ? { ...item, quantity, totalPrice: item.unitPrice * quantity }
          : item
      )
    );
  };

  const handleRemoveProduct = (productId) => {
    setProductsInOrder((prev) =>
      prev.filter((item) => item?.productId !== productId)
    );
  };

  const handleAddProduct = (product) => {
    setProductsInOrder((prev) => [
      ...prev,
      {
        productId: product._id,
        quantity: 1,
        unitPrice: product?.unitPrice,
        totalPrice: product?.unitPrice,
      },
    ]);
    setSelectedProduct(null);
  };

  const columns = [
    {
      title: "Product",
      dataIndex: "productId",
      key: "productId",
      render: (productId) => {
        const product = products.find((prod) => prod?._id === productId);
        return product ? product.name : "Unknown";
      },
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      render: (_, record) => (
        <Input
          type="number"
          min={1}
          value={record.quantity}
          onChange={(e) =>
            handleQuantityChange(record?.productId, Number(e.target.value))
          }
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button danger onClick={() => handleRemoveProduct(record?.productId)}>
          Remove
        </Button>
      ),
    },
  ];

  return (
    <Drawer
      title={isEdit ? "Edit Inventory" : "Add Inventory"}
      width={500}
      onClose={() => setOpen(false)}
      open={open}
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          name="storeId"
          label="Branch"
          rules={[{ required: true, message: "Please select a store!" }]}
        >
          <Select placeholder="Select Branch" allowClear disabled={isEdit}>
            {stores.map((store) => (
              <Option key={store?._id} value={store?._id}>
                {store?.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Total Stock">
          <Input value={totalStock} readOnly />
        </Form.Item>

        <Table
          columns={columns}
          dataSource={productsInOrder}
          rowKey="productId"
          pagination={false}
          footer={() => (
            <div style={{ display: "flex", gap: "10px" }}>
              <Select
                placeholder="Select Product"
                value={selectedProduct?.id}
                onChange={(value) =>
                  setSelectedProduct(
                    products.find((product) => product?._id === value)
                  )
                }
                style={{ flex: 1 }}
              >
                {availableProducts.map((product) => (
                  <Option key={product?._id} value={product?._id}>
                    {product?.name}
                  </Option>
                ))}
              </Select>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() =>
                  selectedProduct && handleAddProduct(selectedProduct)
                }
              >
                Add Product
              </Button>
            </div>
          )}
        />

        <Form.Item>
          <Button type="primary" htmlType="submit" block loading={isLoading}>
            {isEdit ? "Update Inventory" : "Add Inventory"}
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default InventoryForm;
